import React, { useState, useContext, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import StatCard from '../components/StatCard';
import { ThemeContext } from '../contexts/ThemeContext';
import { authFetch } from '../services/authFetch';

// --- POPUP STYLES (copy from ProfilePage.js) ---
const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease-out;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;
const PopupContent = styled.div` 
  background-color: var(--background);
  width: 90%;
  max-width: 500px;
  height: 90vh;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  animation: slideIn 0.3s ease-out;
  border: 1px solid var(--border);
  overflow-y: scroll;
  @keyframes slideIn {
    from { transform: translateY(-20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;
const PopupHeader = styled.div`
  padding: 1.25rem;
  border-bottom: 1px solid var(--border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background-color: #e8f1fd;
  h3 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-dark);
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;
const PopupBody = styled.div`
  padding: 1.75rem;
  font-size: 1.25rem;
  line-height: 1.6;
  color: var(--text-dark);
  background: linear-gradient(to bottom right, var(--background), #f8faff);
  @media (max-width: 576px) {
    padding: 1.25rem;
    font-size: 1.1rem;
  }
`;
const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.75rem;
  color: var(--primary);
  cursor: pointer;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;
  margin-right: -0.5rem;
  &:hover { background-color: rgba(51, 84, 165, 0.1); color: var(--primary); }
  &:active { background-color: rgba(51, 84, 165, 0.2); }
`;
// --- END POPUP STYLES ---

// --- HELPER ---
function formatTanggalIndo(dateString) {
  const bulan = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
  const d = new Date(dateString);
  const tgl = d.getDate();
  const bln = bulan[d.getMonth()];
  const thn = d.getFullYear();
  return `${tgl} ${bln} ${thn}`;
}
// --- END HELPER ---

const StatusSiswaPage = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef(null);

  // Modal states
  const [showKersosDetail, setShowKersosDetail] = useState(false);
  const [showLemburDetail, setShowLemburDetail] = useState(false);
  const [showApresiasiDetail, setShowApresiasiDetail] = useState(false);
  const [showKetidakhadiranDetail, setShowKetidakhadiranDetail] = useState(false);
  const [showSPDetail, setShowSPDetail] = useState(false);

  // Stats data states for selected student
  const [statsData, setStatsData] = useState({
    average_points: 0,
    total_jam_kersos: 0,
    total_jam_lembur: 0,
    total_poin_apresiasi: 0,
    ketidakhadiran: {
      sakit: 0,
      ijin: 0,
      alpha: 0
    },
    surat_peringatan: {
      latest_type: null,
      count: 0
    },
    total_poin_sikap: 0
  });
  const [statsLoading, setStatsLoading] = useState(false);
  
  // Detail data states
  const [kersosDetails, setKersosDetails] = useState([]);
  const [lemburDetails, setLemburDetails] = useState([]);
  const [apresiasiDetails, setApresiasiDetails] = useState([]);
  const [ketidakhadiranDetails, setKetidakhadiranDetails] = useState([]);
  const [spDetails, setSpDetails] = useState([]);
  const [poinSikapDetails, setPoinSikapDetails] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [kompenStats, setKompenStats] = useState({
    sisa_kompen_murni: 0,
    sisa_kompen_alat: 0,
    total_redeemed: 0
  });

  const location = useLocation();
  const navigate = useNavigate();
  const { darkMode } = useContext(ThemeContext);
  
  // Get user role from localStorage
  const userRole = localStorage.getItem('userRole');
  const studentFromLocation = location.state?.student || null;
  
  // Fetch students from API
  const fetchStudents = async () => {
    try {
      setLoading(true);
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
      const response = await authFetch(`${baseUrl}api/teacher/students`);
      
      if (response.ok) {
        const data = await response.json();
        const students = data.data || data;
        setStudents(students);
      } else {
        console.error('Failed to fetch students');
      }
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtered suggestions based on search term
  const filteredSuggestions = students.filter(student => 
    student.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  useEffect(() => {
    if (studentFromLocation) {
      const studentData = getStudentData(studentFromLocation);
      setSelectedStudent(studentData);
      setShowResults(true);
      if (studentFromLocation.id) {
        fetchStudentStats(studentFromLocation.id);
      }
      return; 
    }
    
    fetchStudents();
    
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [studentFromLocation]);

  // Update selectedStudent when statsData changes
  useEffect(() => {
    if (selectedStudent && !statsLoading) {
      // Re-generate student data with updated stats
      const updatedStudentData = {
        ...selectedStudent,
        stats: [
          { 
            label: 'Rata-rata Nilai', 
            value: statsLoading ? '-' : (selectedStudent.average_points || statsData.average_points || 0).toString(), 
            icon: '😇', 
            bgColor: '#e6f8f5' 
          },
          { 
            label: 'Jam Kersos', 
            value: statsLoading ? '-' : (statsData.total_jam_kersos > 0 ? '0' : parseFloat(statsData.total_jam_kersos).toFixed(2)), 
            icon: '🤝', 
            bgColor: '#e8f1fd' 
          },
          { 
            label: 'Jam Lembur', 
            value: statsLoading ? '-' : `${statsData.total_jam_lembur > 0 ? '+' : ''}${parseFloat(statsData.total_jam_lembur).toFixed(2).replace(/\.?0+$/, '')}`, 
            icon: '⏰', 
            bgColor: '#fff3e0' 
          },
          { 
            label: 'Poin Apresiasi', 
            value: statsLoading ? '-' : statsData.total_poin_apresiasi.toString(), 
            icon: '🌟', 
            bgColor: '#ffecf2' 
          },
          { 
            label: 'Ketidakhadiran', 
            value: statsLoading ? '-' : `Sakit: ${statsData.ketidakhadiran.sakit} Hari\nIjin: ${statsData.ketidakhadiran.ijin} Hari\nAlpha: ${statsData.ketidakhadiran.alpha} Hari`, 
            icon: '📋', 
            bgColor: '#f0e8fd' 
          },
          { 
            label: 'Poin Sikap & SP', 
            value: statsLoading 
              ? '-' 
              : `Poin Sikap: ${((parseFloat(statsData.total_poin_sikap) || 0) + 100).toFixed(2).replace(/\.?0+$/, '')}\n\nSP: ${statsData.surat_peringatan.latest_type || '-'}`,
            icon: '⚠️', 
            bgColor: '#ffe6e6' 
          }
        ]
      };
      setSelectedStudent(updatedStudentData);
    }
  }, [statsData, statsLoading]);
  

  
  const handleSuggestionClick = (suggestion) => {
    console.log('Selected student suggestion:', suggestion);
    setSearchTerm(suggestion.name);
    const studentData = getStudentData(suggestion);
    console.log('Generated student data:', studentData);
    setSelectedStudent(studentData);
    // Fetch stats for the selected student
    if (suggestion.id) {
      fetchStudentStats(suggestion.id);
    }
    setShowResults(true);
    setShowSuggestions(false);
  };
  
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && filteredSuggestions.length > 0) {
      handleSuggestionClick(filteredSuggestions[0]);
    }
  };
  
  // Function to generate student data based on student object or name
  const getStudentData = (studentData) => {
    const name = typeof studentData === 'string' ? studentData : studentData.name;
    const studentId = studentData.nis || studentData.id || 'S2023-' + Math.floor(1000 + Math.random() * 9000);
    const grade = studentData.class || studentData.kelas || '11';
    const averagePoints = studentData.average_points || 0; // Store average_points
    const waliKelasName = studentData.wali_kelas_name || null;
    
    // Note: fetchStudentStats is called from the handlers that use this function
    
    return {
      id: studentData.id,
      name: name,
      nickname: name.split(' ')[0], // Get first name
      grade: grade,
      studentId: studentId,
      average_points: averagePoints, // Store it in the object
      wali_kelas_name: waliKelasName,
      absenceNumber: studentData.absenceNumber || Math.floor(Math.random() * 20).toString(),
      homeAddress: studentData.homeAddress || 'Jl. Pahlawan No. 45, Surakarta',
      boardingAddress: studentData.boardingAddress || 'Jl. Slamet Riyadi No. 21, Surakarta',
      childNumber: studentData.childNumber || '2',
      numberOfSiblings: studentData.numberOfSiblings || '3',
      fatherName: studentData.fatherName || 'Bambang Susilo',
      motherName: studentData.motherName || 'Sri Rahayu',
      emergencyContact: studentData.emergencyContact || '+62 812-3456-7890',
      avatarUrl: studentData.avatarUrl
        ? (studentData.avatarUrl.startsWith('http')
            ? studentData.avatarUrl
            : `${process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/'}${studentData.avatarUrl.startsWith('/') ? studentData.avatarUrl : studentData.avatarUrl}`)
        : '/images/user.png',
      stats: [
        { 
          label: 'Rata-rata Nilai', 
          value: averagePoints.toString(), 
          icon: '😇', 
          bgColor: '#e6f8f5' 
        },
        { 
          label: 'Jam Kersos', 
          value: '-', 
          icon: '🤝', 
          bgColor: '#e8f1fd' 
        },
        { 
          label: 'Jam Lembur', 
          value: '-', 
          icon: '⏰', 
          bgColor: '#fff3e0' 
        },
        { 
          label: 'Poin Apresiasi', 
          value: '-', 
          icon: '🌟', 
          bgColor: '#ffecf2' 
        },
        { 
          label: 'Ketidakhadiran', 
          value: '-', 
          icon: '📋', 
          bgColor: '#f0e8fd' 
        },
        { 
          label: 'Poin Sikap & SP', 
          value: '-', 
          icon: '⚠️', 
          bgColor: '#ffe6e6' 
        }
      ]
    };
  };

  // Fetch student statistics data
  const fetchStudentStats = async (studentId) => {
    console.log('Fetching stats for student ID:', studentId);
    setStatsLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
      
      // Determine API prefix based on user role
      const apiPrefix = userRole === 'admin_user' ? 'admin' : 'teacher';
      
      // First, fetch student profile data to get average_points and wali_kelas_name
      try {
        const studentProfileResponse = await authFetch(`${baseUrl}api/students/${studentId}`);
        if (studentProfileResponse.ok) {
          const studentProfileData = await studentProfileResponse.json();
          const profile = studentProfileData.data || studentProfileData;
          
          // Update statsData with profile information
          setStatsData(prev => ({
            ...prev,
            average_points: profile.average_points || 0
          }));
          
          // Update selectedStudent with wali_kelas_name if available
          if (profile.wali_kelas_name || profile.homeroom_teacher) {
            setSelectedStudent(prev => ({
              ...prev,
              wali_kelas_name: profile.wali_kelas_name || profile.homeroom_teacher
            }));
          } else if (profile.class) {
            // If no wali_kelas_name in profile, try to fetch homeroom teacher by class
            try {
              const teachersResponse = await authFetch(`${baseUrl}api/teachers`);
              if (teachersResponse.ok) {
                const teachersData = await teachersResponse.json();
                const teachers = teachersData.data || teachersData || [];
                const homeroomTeacher = teachers.find(teacher => teacher.headOfClass === profile.class);
                if (homeroomTeacher) {
                  setSelectedStudent(prev => ({
                    ...prev,
                    wali_kelas_name: homeroomTeacher.name
                  }));
                }
              }
            } catch (teacherError) {
              console.error('Error fetching homeroom teacher:', teacherError);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching student profile:', error);
      }
      
      // Fetch all statistics data in parallel for the specific student
      const [
        kersosResponse,
        lemburResponse,
        apresiasiResponse,
        ketidakhadiranResponse,
        spResponse,
        poinSikapResponse
      ] = await Promise.all([
        authFetch(`${baseUrl}api/${apiPrefix}/student/${studentId}/jam-kersos`),  
        authFetch(`${baseUrl}api/${apiPrefix}/student/${studentId}/jam-lembur`), 
        authFetch(`${baseUrl}api/${apiPrefix}/student/${studentId}/poin-apresiasi`),
        authFetch(`${baseUrl}api/${apiPrefix}/student/${studentId}/ketidakhadiran`), 
        authFetch(`${baseUrl}api/${apiPrefix}/student/${studentId}/surat-peringatan`),
        authFetch(`${baseUrl}api/${apiPrefix}/student/${studentId}/poin-sikap`)
      ]);

      console.log('API responses:', {
        kersos: kersosResponse.status,
        lembur: lemburResponse.status,
        apresiasi: apresiasiResponse.status,
        ketidakhadiran: ketidakhadiranResponse.status,
        sp: spResponse.status,
        poinSikap: poinSikapResponse.status
      });

      // Process kersos data
      if (kersosResponse.ok) {
        const kersosData = await kersosResponse.json();
        console.log('Kersos data:', kersosData);
        const kersosRecords = kersosData.data || [];
        const totalKersos = kersosRecords.reduce((sum, record) => {
          const hour = parseFloat(record.hour) || 0;
          return sum + hour;
        }, 0);
        console.log('Total kersos calculated:', totalKersos);
        setKersosDetails(kersosRecords.map(record => ({
          tanggal: record.date || record.created_at,
          pelanggaran: record.pelanggaran,
          description: record.description,
          jam: record.hour > 0 ? `+${record.hour}` : record.hour.toString()
        })));
        setStatsData(prev => ({ ...prev, total_jam_kersos: totalKersos }));
      } else {
        console.error('Kersos API failed:', kersosResponse.status);
      }

      // Process lembur data
      if (lemburResponse.ok) {
        const lemburData = await lemburResponse.json();
        const lemburRecords = lemburData.data || [];
        const totalLembur = lemburRecords.reduce((sum, record) => {
          const hour = parseFloat(record.hour) || 0;
          return sum + hour;
        }, 0);
        setLemburDetails(lemburRecords.map(record => ({
          jenis: record.type || 'Lembur',
          tanggal: record.date || record.created_at,
          keterangan: record.description,
          kategori: record.pelanggaran?.description || 'N/A',
          jam: record.hour > 0 ? `+${parseFloat(record.hour).toFixed(2).replace(/\.?0+$/, '')}` : parseFloat(record.hour).toFixed(2).replace(/\.?0+$/, '')
        })));
        setStatsData(prev => ({ ...prev, total_jam_lembur: totalLembur }));
        
        // Set kompen stats if available
        if (lemburData.kompen_stats) {
          setKompenStats(lemburData.kompen_stats);
        }
      }

      // Process apresiasi data
      if (apresiasiResponse.ok) {
        const apresiasiData = await apresiasiResponse.json();
        const apresiasiRecords = apresiasiData.data || [];
        const totalApresiasi = apresiasiRecords.reduce((sum, record) => {
          const point = parseFloat(record.point) || 0;
          return sum + point;
        }, 0);
        setApresiasiDetails(apresiasiRecords.map(record => ({
          tanggal: record.date || record.created_at,
          jenis: record.description,
          poin: record.point > 0 ? `+${record.point}` : record.point.toString()
        })));
        setStatsData(prev => ({ ...prev, total_poin_apresiasi: totalApresiasi }));
      }

      // Process ketidakhadiran data
      if (ketidakhadiranResponse.ok) {
        const ketidakhadiranData = await ketidakhadiranResponse.json();
        const ketidakhadiranRecords = ketidakhadiranData.data || [];
        console.log('Ketidakhadiran records:', ketidakhadiranRecords);
        const ketidakhadiranStats = ketidakhadiranRecords.reduce((acc, record) => {
          const type = record.type?.toLowerCase();
          if (type === 'sakit') acc.sakit += record.days || 1;
          else if (type === 'ijin') acc.ijin += record.days || 1;
          else if (type === 'alfa' || type === 'alpha') acc.alpha += record.days || 1;
          return acc;
        }, { sakit: 0, ijin: 0, alpha: 0 });
        
        // Fetch class type information for each ketidakhadiran record
        // Since the individual record endpoints don't exist, we'll try to get more accurate data
        // by cross-referencing with any existing permit data
        const ketidakhadiranDetailsWithKelas = ketidakhadiranRecords.map(record => ({
          laporan_kts_id: record.laporan_kts_id,
          ijin_tidakmasuk_id: record.ijin_tidakmasuk_id,
          tanggal: record.created_at,
          jenis: record.type,
          kelas: record.kelas || '-', // Use the 'kelas' from the record, or fallback to '-'
          hari: record.days || 1,
          kategori: record.student_description || record.type,
          keterangan: record.laporan_description || record.description
        }));
        
        setKetidakhadiranDetails(ketidakhadiranDetailsWithKelas);
        setStatsData(prev => ({ ...prev, ketidakhadiran: ketidakhadiranStats }));
      }

      // Process surat peringatan data
      if (spResponse.ok) {
        const spData = await spResponse.json();
        const spRecords = spData.data || [];
        // Filter only approved SPs with status 'terima'
        const approvedSP = spRecords.filter(sp => sp.status === 'terima');
        // Sort by created_at to get the actual latest SP
        const sortedSP = approvedSP.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        const latestSP = sortedSP.length > 0 ? sortedSP[0] : null;
        
        setSpDetails(approvedSP.map(record => ({
          tanggal: record.created_at || record.decision_date,
          jenis: record.type,
          diterbitkan: record.decision_by?.name || record.issued_by || 'Admin'
        })));
        setStatsData(prev => ({ 
          ...prev, 
          surat_peringatan: {
            latest_type: latestSP?.type || null,
            count: approvedSP.length
          }
        }));
      }

      // Process poin sikap data
      if (poinSikapResponse.ok) {
        const poinSikapData = await poinSikapResponse.json();
        if (poinSikapData.success) {
            setStatsData(prev => ({ ...prev, total_poin_sikap: poinSikapData.data.total_poin_sikap }));
        }
      }

    } catch (error) {
      console.error('Failed to fetch student statistics:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchPoinSikapDetails = async (studentId) => {
    setDetailsLoading(true);
    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/';
      const apiPrefix = userRole === 'admin_user' ? 'admin' : 'teacher';
      const response = await authFetch(`${baseUrl}api/${apiPrefix}/student/${studentId}/poin-sikap-details`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setPoinSikapDetails(data.data || []);
        } else {
          setPoinSikapDetails([]);
        }
      } else {
        setPoinSikapDetails([]);
      }
    } catch (error) {
      console.error('Failed to fetch poin sikap details:', error);
      setPoinSikapDetails([]);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleShowSPDetail = async (studentId) => {
    await fetchPoinSikapDetails(studentId);
    setShowSPDetail(true);
  };

  return (
    <PageContainer>
      <PageHeader>
        {studentFromLocation && (
          <BackButton onClick={() => navigate(-1)}>
            ← Kembali
          </BackButton>
        )}
        <h1>Status Siswa</h1>
      </PageHeader>
      
      {!studentFromLocation && (
        <SearchSection>
          <SearchTitle>Siapakah yang Anda cari?</SearchTitle>
          <SearchForm>
            <SearchInputContainer ref={searchRef}>
              <SearchLabel htmlFor="studentName">Nama Siswa:</SearchLabel>
              <SearchInputWrapper>
                <SearchInput
                  type="text"
                  id="studentName"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  onKeyDown={handleKeyDown}
                  placeholder="Masukkan nama siswa"
                  autoComplete="off"
                />
                {showSuggestions && searchTerm.length > 0 && (
                  <SuggestionsList>
                    {loading ? (
                      <SuggestionItem>Memuat data siswa...</SuggestionItem>
                    ) : filteredSuggestions.length > 0 ? (
                      filteredSuggestions.map((suggestion, index) => (
                        <SuggestionItem
                          key={suggestion.id || index}
                          onClick={() => handleSuggestionClick(suggestion)}
                        >
                          {suggestion.name} <span style={{ color: '#888', fontSize: '0.875rem' }}>({suggestion.class || suggestion.kelas})</span>
                        </SuggestionItem>
                      ))
                    ) : (
                      <SuggestionItem>Tidak ada hasil</SuggestionItem>
                    )}
                  </SuggestionsList>
                )}
              </SearchInputWrapper>

            </SearchInputContainer>
          </SearchForm>
        </SearchSection>
      )}
      
      {showResults && selectedStudent && (
        <ProfileCard>
          <ProfileHeader>
            <AvatarContainer>
              <Avatar 
                src={selectedStudent.avatarUrl} 
                alt={selectedStudent.name}
                onError={(e) => {
                  e.target.src = '/images/user.png';
                }}
              />
            </AvatarContainer>
            <ProfileInfo>
              <h2>{selectedStudent.name}</h2>
              <InfoRow>
                <InfoItem>Kelas: {selectedStudent.grade}</InfoItem>
                <InfoDivider>|</InfoDivider>
                <InfoItem>NIS: {selectedStudent.studentId}</InfoItem>
                {selectedStudent.wali_kelas_name && (
                  <>
                    <InfoDivider>|</InfoDivider>
                    <InfoItem>Wali Kelas: {selectedStudent.wali_kelas_name}</InfoItem>
                  </>
                )}
              </InfoRow>
            </ProfileInfo>
          </ProfileHeader>
          
          <StatGrid>
            {selectedStudent.stats.map((stat, index) => (
              <StatCard 
                key={index}
                label={stat.label}
                value={stat.value}
                icon={stat.icon}
                bgColor={stat.bgColor}
                onClick={
                  stat.label === 'Jam Kersos'
                    ? () => setShowKersosDetail(true)
                    : stat.label === 'Jam Lembur'
                      ? () => setShowLemburDetail(true)
                      : stat.label === 'Poin Apresiasi'
                        ? () => setShowApresiasiDetail(true)
                        : stat.label === 'Ketidakhadiran' || stat.label === 'Ketidakhadiran'
                          ? () => setShowKetidakhadiranDetail(true)
                          : stat.label === 'Poin Sikap & SP'
                            ? () => handleShowSPDetail(selectedStudent.id)
                            : undefined
                }
                style={{ cursor: stat.label === 'Jam Kersos' || stat.label === 'Jam Lembur' || stat.label === 'Poin Apresiasi' || stat.label === 'Ketidakhadiran' || stat.label === 'Rekap Piket' || stat.label === 'Poin Sikap & SP' ? 'pointer' : 'default' }}
              />
            ))}
          </StatGrid>

          {/* POPUPS: identik dengan ProfilePage.js */}
          {showKersosDetail && (
            <PopupOverlay>
              <PopupContent style={{ maxWidth: 600 }}>
                <PopupHeader>
                  <h3>
                    <span role="img" aria-label="Jam Kersos">🤝</span>
                    Detail Jam Kersos
                  </h3>
                  <CloseButton onClick={() => setShowKersosDetail(false)}>×</CloseButton>
                </PopupHeader>
                <PopupBody>
                  {/* Ketidaksesuaian Table (Negative Hours) */}
                  {kersosDetails.filter(row => parseFloat(row.jam) < 0).length > 0 && (
                    <div style={{ marginBottom: '2rem' }}>
                      <h4 style={{ 
                        color: '#d32f2f', 
                        fontWeight: 600, 
                        fontSize: '1.1rem', 
                        marginBottom: '1rem',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <span style={{ marginRight: '8px' }}>⚠️</span>
                        Ketidaksesuaian
                      </h4>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1rem' }}>
                          <thead>
                            <tr style={{ background: '#ffebee' }}>
                              <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Tanggal</th>
                              <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Jenis Pelanggaran</th>
                              <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Jam Kersos</th>
                            </tr>
                          </thead>
                          <tbody>
                            {kersosDetails.filter(row => parseFloat(row.jam) < 0).map((row, idx) => (
                              <tr key={idx}>
                                <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{formatTanggalIndo(row.tanggal)}</td>

                                <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>
                                  {row.pelanggaran && row.pelanggaran !== 'N/A' && (
                                    <>
                                      <span style={{ fontSize: '13px' }}>Kategori: {row.pelanggaran}</span>
                                      <hr style={{ height: '1px', backgroundColor: 'rgb(219 213 213)', border: 'none' }}/>
                                    </>
                                  )}
                                  { row.description }
                                </td>

                                <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center', color: '#d32f2f', fontWeight: 600 }}>{row.jam}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Penyesuaian Table (Positive Hours) */}
                  {kersosDetails.filter(row => parseFloat(row.jam) > 0).length > 0 && (
                    <div>
                      <h4 style={{ 
                        color: '#2e7d32', 
                        fontWeight: 600, 
                        fontSize: '1.1rem', 
                        marginBottom: '1rem',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <span style={{ marginRight: '8px' }}>✅</span>
                        Penyesuaian
                      </h4>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1rem' }}>
                          <thead>
                            <tr style={{ background: '#e8f5e8' }}>
                              <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Tanggal</th>
                              <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Jenis Penyesuaian</th>
                              <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Jam Kersos</th>
                            </tr>
                          </thead>
                          <tbody>
                            {kersosDetails.filter(row => parseFloat(row.jam) > 0).map((row, idx) => (
                              <tr key={idx}>
                                <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{formatTanggalIndo(row.tanggal)}</td>
                                <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>{row.pelanggaran}</td>
                                <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center', color: '#2e7d32', fontWeight: 600 }}>{row.jam}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Show message if no data */}
                  {kersosDetails.length === 0 && (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '2rem', 
                      color: '#666', 
                      fontStyle: 'italic' 
                    }}>
                      Tidak ada data jam kersos
                    </div>
                  )}
                </PopupBody>
              </PopupContent>
            </PopupOverlay>
          )}

          {showLemburDetail && (
            <PopupOverlay>
              <PopupContent style={{ maxWidth: 600 }}>
                <PopupHeader>
                  <h3>
                    <span role="img" aria-label="Jam Lembur">⏰</span>
                    Detail Jam Lembur
                  </h3>
                  <CloseButton onClick={() => setShowLemburDetail(false)}>×</CloseButton>
                </PopupHeader>
                <PopupBody>
                  {/* Ketidaksesuaian Table (Negative Hours) */}
                  {lemburDetails.filter(row => parseFloat(row.jam) < 0).length > 0 && (
                    <div style={{ marginBottom: '2rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '1rem' }}>
                        <h4 style={{ 
                          color: '#d32f2f', 
                          fontWeight: 600, 
                          fontSize: '1.1rem', 
                          margin: 0,
                          display: 'flex',
                          alignItems: 'center'
                        }}>
                          <span style={{ marginRight: '8px' }}>⚠️</span>
                          Ketidaksesuaian
                        </h4>
                        
                        {/* Tooltip Icon */}
                        <div style={{ position: 'relative', display: 'inline-block' }}>
                          <span 
                            style={{ 
                              cursor: 'help',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '20px',
                              height: '20px',
                              borderRadius: '50%',
                              backgroundColor: '#2196F3',
                              color: 'white',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              userSelect: 'none'
                            }}
                            title={`Sisa Kompen Murni: ${kompenStats.sisa_kompen_murni} jam\nJam lembur karena ketidakhadiran di bengkel. Jam lembur harus bernilai 0 sebagai salah satu syarat kenaikan dan kelulusan.\n\nSisa Kompen Alat: ${kompenStats.sisa_kompen_alat} jam\nJam lembur karena alat/benda. Jam lembur bisa ditutup dengan jam surplus atau dialihkan ke tahun depan. Ijazah tidak akan diberikan jika jam lembur belum 0.`}
                          >
                            ?
                          </span>
                        </div>
                      </div>
                      
                      {/* Info boxes for Kompen Stats */}
                      <div style={{ 
                        display: 'grid', 
                        gridTemplateColumns: '1fr 1fr', 
                        gap: '12px', 
                        marginBottom: '1rem',
                        fontSize: '0.9rem'
                      }}>
                        <div style={{ 
                          padding: '12px', 
                          backgroundColor: '#fff3e0', 
                          borderRadius: '8px',
                          border: '1px solid #ffb74d'
                        }}>
                          <div style={{ fontWeight: 600, color: '#e65100', marginBottom: '4px' }}>
                            Sisa Kompen Murni
                          </div>
                          <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#d32f2f' }}>
                            {kompenStats.sisa_kompen_murni} jam
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                            Jam lembur karena ketidakhadiran di bengkel. Harus 0 untuk syarat kenaikan dan kelulusan.
                          </div>
                        </div>
                        
                        <div style={{ 
                          padding: '12px', 
                          backgroundColor: '#e3f2fd', 
                          borderRadius: '8px',
                          border: '1px solid #64b5f6'
                        }}>
                          <div style={{ fontWeight: 600, color: '#0d47a1', marginBottom: '4px' }}>
                            Sisa Kompen Alat
                          </div>
                          <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#d32f2f' }}>
                            {kompenStats.sisa_kompen_alat} jam
                          </div>
                          <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '4px' }}>
                            Jam lembur karena alat/benda. Bisa ditutup dengan jam surplus atau dialihkan ke tahun depan. Ijazah tidak akan diberikan jika jam lembur belum 0.
                          </div>
                        </div>
                      </div>
                      
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1rem' }}>
                          <thead>
                            <tr style={{ background: '#ffebee' }}>
                              <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Tanggal</th>
                              <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Keterangan</th>
                              <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Jumlah Jam</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lemburDetails.filter(row => parseFloat(row.jam) < 0).map((row, idx) => (
                              <tr key={idx}>
                                <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{formatTanggalIndo(row.tanggal)}</td>
                                <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>
                                  
                                  {row.kategori && row.kategori !== 'N/A' && (
                                    <>
                                      <span style={{ fontSize: '13px' }}>Kategori: {row.kategori}</span>
                                      <hr style={{ height: '1px', backgroundColor: 'rgb(219 213 213)', border: 'none' }}/>
                                    </>
                                  )}
                                  {row.keterangan}</td>
                                <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center', color: '#d32f2f', fontWeight: 600 }}>{row.jam}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Penyesuaian Table (Positive Hours) */}
                  {lemburDetails.filter(row => parseFloat(row.jam) > 0).length > 0 && (
                    <div>
                      <h4 style={{ 
                        color: '#2e7d32', 
                        fontWeight: 600, 
                        fontSize: '1.1rem', 
                        marginBottom: '1rem',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <span style={{ marginRight: '8px' }}>✅</span>
                        Penyesuaian
                      </h4>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1rem' }}>
                          <thead>
                            <tr style={{ background: '#e8f5e8' }}>
                              <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Tanggal</th>
                              <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Keterangan</th>
                              <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Jumlah Jam</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lemburDetails.filter(row => parseFloat(row.jam) > 0).map((row, idx) => (
                              <tr key={idx}>
                                <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{formatTanggalIndo(row.tanggal)}</td>
                                <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>{row.keterangan}</td>
                                <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center', color: '#2e7d32', fontWeight: 600 }}>{row.jam}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Show message if no data */}
                  {lemburDetails.filter(row => parseFloat(row.jam) !== 0).length === 0 && (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '2rem', 
                      color: '#666', 
                      fontStyle: 'italic' 
                    }}>
                      Tidak ada data jam lembur
                    </div>
                  )}
                </PopupBody>
              </PopupContent>
            </PopupOverlay>
          )}

          {showApresiasiDetail && (
            <PopupOverlay>
              <PopupContent style={{ maxWidth: 600 }}>
                <PopupHeader>
                  <h3>
                    <span role="img" aria-label="Poin Apresiasi">🌟</span>
                    Detail Poin Apresiasi
                  </h3>
                  <CloseButton onClick={() => setShowApresiasiDetail(false)}>×</CloseButton>
                </PopupHeader>
                <PopupBody>
                  {/* Ketidaksesuaian Table (Negative Points) */}
                  {apresiasiDetails.filter(row => parseFloat(row.poin) < 0).length > 0 && (
                    <div style={{ marginBottom: '2rem' }}>
                      <h4 style={{ 
                        margin: '0 0 1rem 0', 
                        fontWeight: 600, 
                        color: '#c01c28', 
                        display: 'flex', 
                        alignItems: 'center' 
                      }}>
                        <span style={{ marginRight: '8px', fontSize: '1.2rem' }}>⚠️</span>
                        Ketidaksesuaian
                      </h4>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1rem' }}>
                          <thead>
                            <tr style={{ background: '#ffebee' }}>
                              <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Tanggal</th>
                              <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Jenis Pelanggaran</th>
                              <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Poin Apresiasi</th>
                            </tr>
                          </thead>
                          <tbody>
                            {apresiasiDetails.filter(row => parseFloat(row.poin) < 0).map((row, idx) => (
                              <tr key={idx}>
                                <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{formatTanggalIndo(row.tanggal)}</td>
                                <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>{row.jenis}</td>
                                <td style={{ 
                                  padding: '8px', 
                                  border: '1px solid #e0e0e0', 
                                  textAlign: 'center',
                                  color: '#c01c28',
                                  fontWeight: 'bold'
                                }}>{row.poin}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Penyesuaian Table (Positive Points) */}
                  {apresiasiDetails.filter(row => parseFloat(row.poin) > 0).length > 0 && (
                    <div style={{ marginBottom: '1rem' }}>
                      <h4 style={{ 
                        margin: '0 0 1rem 0', 
                        fontWeight: 600, 
                        color: '#2ec27e', 
                        display: 'flex', 
                        alignItems: 'center' 
                      }}>
                        <span style={{ marginRight: '8px', fontSize: '1.2rem' }}>✅</span>
                        Penyesuaian
                      </h4>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1rem' }}>
                          <thead>
                            <tr style={{ background: '#e8f5e8' }}>
                              <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Tanggal</th>
                              <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Jenis Apresiasi</th>
                              <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Poin Apresiasi</th>
                            </tr>
                          </thead>
                          <tbody>
                            {apresiasiDetails.filter(row => parseFloat(row.poin) > 0).map((row, idx) => (
                              <tr key={idx}>
                                <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{formatTanggalIndo(row.tanggal)}</td>
                                <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>{row.jenis}</td>
                                <td style={{ 
                                  padding: '8px', 
                                  border: '1px solid #e0e0e0', 
                                  textAlign: 'center',
                                  color: '#2ec27e',
                                  fontWeight: 'bold'
                                }}>{row.poin}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* No Data Message */}
                  {apresiasiDetails.length === 0 && (
                    <div style={{ 
                      textAlign: 'center', 
                      padding: '2rem', 
                      color: '#666', 
                      fontStyle: 'italic' 
                    }}>
                      Tidak ada data poin apresiasi
                    </div>
                  )}
                </PopupBody>
              </PopupContent>
            </PopupOverlay>
          )}

          {showKetidakhadiranDetail && (
            <PopupOverlay>
              <PopupContent style={{ maxWidth: 900 }}>
                <PopupHeader style={{ background: 'unset' }}>
                  <h3 style={{ display: 'flex', alignItems: 'center', fontWeight: 700, fontSize: '1.5rem', margin: 0 }}>
                    <span role="img" aria-label="Ketidakhadiran" style={{ marginRight: 12, fontSize: '2rem' }}>📋</span>
                    Detail Ketidakhadiran
                  </h3>
                  <CloseButton onClick={() => setShowKetidakhadiranDetail(false)}>×</CloseButton>
                </PopupHeader>
                <PopupBody>
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1rem' }}>
                      <thead>
                        <tr style={{ background: '#f0e8fd' }}>
                          <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Tgl Lapor</th>
                          <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Jenis</th>
                          <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Kelas</th>
                          <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Hari</th>
                          <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Keterangan</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ketidakhadiranDetails.map((row, idx) => (
                          <tr key={idx}>
                            <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{formatTanggalIndo(row.tanggal)}</td>
                            <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>{row.jenis}</td>
                            <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{row.kelas}</td>
                            <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{row.hari}</td>

                            <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>
                            {row.laporan_kts_id !== null && (
                              <>
                                <span style={{ fontSize: '13px' }}>{row.kategori}</span>
                                <hr style={{ height: '1px', backgroundColor: 'rgb(219 213 213)', border: 'none' }}/>
                                {row.keterangan}
                              </>
                            )}
                             {row.ijin_tidakmasuk_id !== null && (
                              <>
                                {row.keterangan}
                              </>
                            )}
                            </td>
                          </tr> 
                        ))}
                      </tbody>
                    </table>
                  </div>
                </PopupBody>
              </PopupContent>
            </PopupOverlay>
          )}

          {showSPDetail && (
            <PopupOverlay>
              <PopupContent style={{ maxWidth: 600 }}>
                <PopupHeader>
                  <h3>
                    <span role="img" aria-label="Surat Peringatan">⚠️</span>
                    Detail SP & Poin Sikap
                  </h3>
                  <CloseButton onClick={() => setShowSPDetail(false)}>×</CloseButton>
                </PopupHeader>
                <PopupBody>
                  {detailsLoading ? (
                    <div style={{ textAlign: 'center', padding: '2rem' }}>Memuat rincian...</div>
                  ) : (
                    <>
                      <div style={{ marginBottom: '2rem' }}>
                        <h4 style={{ color: '#333', fontWeight: 600, fontSize: '1.1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                          <span style={{ marginRight: '8px' }}>🚫</span>
                          Rincian Poin Sikap
                        </h4>
                        {poinSikapDetails.filter(row => row.point < 0).length > 0 && (
                          <div style={{ marginBottom: '1rem' }}>
                            <h5 style={{ color: '#d32f2f', fontWeight: 600, fontSize: '1rem', marginBottom: '0.5rem' }}>Ketidaksesuaian</h5>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1rem' }}>
                              <thead><tr style={{ background: '#ffebee' }}>
                                <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Tanggal</th>
                                <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Keterangan</th>
                                <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Poin</th>
                              </tr></thead>
                              <tbody>{poinSikapDetails.filter(row => row.point < 0).map((row, idx) => (
                                <tr key={idx}><td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{formatTanggalIndo(row.created_at)}</td>

                                <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>
 
                                  {row.pelanggaran && row.pelanggaran !== 'N/A' && (
                                    <>
                                      <span style={{ fontSize: '13px' }}>Kategori: {row.pelanggaran}</span>
                                      <hr style={{ height: '1px', backgroundColor: 'rgb(219 213 213)', border: 'none' }}/>
                                    </>
                                  )}
                                  {row.description}
                                </td>

                                <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center', color: '#d32f2f', fontWeight: 600 }}>{row.point}</td></tr>
                              ))}</tbody>
                            </table>
                          </div>
                        )}
                        {poinSikapDetails.filter(row => row.point > 0).length > 0 && (
                          <div>
                            <h5 style={{ color: '#2e7d32', fontWeight: 600, fontSize: '1rem', marginBottom: '0.5rem' }}>Penyesuaian</h5>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1rem' }}>
                              <thead><tr style={{ background: '#e8f5e8' }}>
                                <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Tanggal</th>
                                <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Keterangan</th>
                                <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Poin</th>
                              </tr></thead>
                              <tbody>{poinSikapDetails.filter(row => row.point > 0).map((row, idx) => (
                                <tr key={idx}><td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{formatTanggalIndo(row.created_at)}</td>
                                <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>{row.description}</td>
                                <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center', color: '#2e7d32', fontWeight: 600 }}>{`+${row.point}`}</td></tr>
                              ))}</tbody>
                            </table>
                          </div>
                        )}
                        {poinSikapDetails.length === 0 && (
                          <div style={{ textAlign: 'center', padding: '1rem', color: '#666', fontStyle: 'italic' }}>Tidak ada data poin sikap.</div>
                        )}
                      </div>

                      <h4 style={{ color: '#333', fontWeight: 600, fontSize: '1.1rem', marginBottom: '1rem', display: 'flex', alignItems: 'center' }}>
                        <span style={{ marginRight: '8px' }}>📄</span>
                        Riwayat Surat Peringatan
                      </h4>
                      {spDetails.length > 0 ? (
                        <div style={{ overflowX: 'auto' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '1rem' }}>
                            <thead>
                              <tr style={{ background: '#ffe6e6' }}>
                                <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Tanggal</th>
                                <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Jenis SP</th>
                                <th style={{ padding: '8px', border: '1px solid #e0e0e0' }}>Diterbitkan Oleh</th>
                              </tr>
                            </thead>
                            <tbody>
                              {spDetails.map((row, idx) => (
                                <tr key={idx}>
                                  <td style={{ padding: '8px', border: '1px solid #e0e0e0', textAlign: 'center' }}>{formatTanggalIndo(row.tanggal)}</td>
                                  <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>{row.jenis}</td>
                                  <td style={{ padding: '8px', border: '1px solid #e0e0e0' }}>{row.diterbitkan}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                         <div style={{ textAlign: 'center', padding: '1rem', color: '#666', fontStyle: 'italic' }}>
                           Tidak ada data surat peringatan
                         </div>
                      )}
                    </>
                  )}
                </PopupBody>
              </PopupContent>
            </PopupOverlay>
          )}
        
        </ProfileCard>
      )}
    </PageContainer>
  );
};

const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 1.5rem;
  
  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
  }
`;

const ProfileCard = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: var(--box-shadow);
  margin-bottom: 1.5rem;
  transition: box-shadow 0.3s ease, background-color 0.3s;
  
  &:hover {
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15);
  }
`;

const ProfileHeader = styled.div`
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;
  border-bottom: 1px solid var(--border);
  
  @media (max-width: 576px) {
    flex-direction: row;
    text-align: left;
    padding: 1rem;
    gap: 1rem;
  }
`;

const AvatarContainer = styled.div`
  flex-shrink: 0;
`;

const Avatar = styled.img`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  
  @media (max-width: 576px) {
    width: 60px;
    height: 60px;
    border-width: 2px;
  }
`;

const ProfileInfo = styled.div`
  h2 {
    font-size: 1.5rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }
  
  @media (max-width: 576px) {
    h2 {
      font-size: 1.25rem;
      margin-bottom: 0.25rem;
    }
  }
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-top: 5px;
  
  @media (max-width: 576px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
`;

const InfoItem = styled.div`
  color: var(--text-dark);
  font-size: 1.125rem;
  font-weight: 500;
`;

const InfoDivider = styled.span`
  color: #ddd;
  font-weight: 300;
  @media (max-width: 576px) {
    display: none;
  }
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  padding: 1rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
    padding: 0.5rem;
  }
`;

const SearchSection = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: var(--box-shadow);
  margin-bottom: 1.5rem;
`;

const SearchTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: var(--text-dark);
`;

const SearchForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SearchInputContainer = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  position: relative;
  
  @media (max-width: 576px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: var(--text-dark);
`;

const SearchInputWrapper = styled.div`
  position: relative;
  flex: 1;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  font-family: inherit;
  font-size: 1rem;
  color: var(--text-dark);
  background-color: var(--background-light);
  
  &:focus {
    border-color: var(--primary);
    box-shadow: 0 0 0 2px rgba(43, 93, 161, 0.2);
    outline: none;
  }
`;



const SuggestionsList = styled.ul`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: var(--background);
  border: 1px solid var(--border);
  border-radius: 0 0 8px 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  list-style: none;
  padding: 0;
  margin: 0;
  max-height: 200px;
  overflow-y: auto;
  z-index: 10;
`;

const SuggestionItem = styled.li`
  padding: 0.75rem 1rem;
  cursor: pointer;
  border-bottom: 1px solid var(--border);
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: var(--background-light);
  }
`;

const BackButton = styled.button`
  background-color: var(--primary);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s, transform 0.1s;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  
  &:hover {
    background-color: var(--primary-light);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

export default StatusSiswaPage;